import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  orderBy,
  query as firebaseQuery,
  where,
  QueryConstraint,
} from 'firebase/firestore'
import {PrintStoryDoc} from './types'

const mapDocs = (snapshot: any, idField?: string): PrintStoryDoc[] =>
  snapshot.docs.map((document: any) => {
    const data = document.data()
    return {
      id: document.id,
      ...data,
      ...(idField ? {[idField]: data[idField] || document.id} : {}),
    }
  })

export const getDocsByContentId = async (
  collectionName: string,
  contentId: string,
  idField?: string
): Promise<PrintStoryDoc[]> => {
  const db = getFirestore()
  const snapshot = await getDocs(
    firebaseQuery(collection(db, collectionName), where('contentId', '==', contentId))
  )
  return mapDocs(snapshot, idField)
}

export const getDocsByDeviceId = async (
  collectionName: string,
  deviceId: string,
  orderField: string,
  max = 200,
  idField?: string
): Promise<PrintStoryDoc[]> => {
  const db = getFirestore()
  const constraints: QueryConstraint[] = [
    where('deviceId', '==', deviceId),
    orderBy(orderField, 'desc'),
    limit(max),
  ]

  try {
    const snapshot = await getDocs(firebaseQuery(collection(db, collectionName), ...constraints))
    return mapDocs(snapshot, idField)
  } catch (error) {
    // Fallback without orderBy if composite index is missing
    console.warn(`[print-story] Falling back for ${collectionName}`, error)
    const snapshot = await getDocs(
      firebaseQuery(collection(db, collectionName), where('deviceId', '==', deviceId), limit(max))
    )
    return mapDocs(snapshot, idField)
  }
}

export const getDocsByJobId = async (
  collectionName: string,
  jobId: string,
  orderField: string,
  max = 50,
  idField?: string
): Promise<PrintStoryDoc[]> => {
  const db = getFirestore()
  try {
    const snapshot = await getDocs(
      firebaseQuery(
        collection(db, collectionName),
        where('jobId', '==', jobId),
        orderBy(orderField, 'desc'),
        limit(max)
      )
    )
    return mapDocs(snapshot, idField)
  } catch (error) {
    console.warn(`[print-story] Falling back job query for ${collectionName}`, error)
    const snapshot = await getDocs(
      firebaseQuery(collection(db, collectionName), where('jobId', '==', jobId), limit(max))
    )
    return mapDocs(snapshot, idField)
  }
}

export const getRawContentDoc = async (contentId: string): Promise<PrintStoryDoc | null> => {
  const db = getFirestore()
  const snapshot = await getDoc(doc(db, '_contents', contentId))
  if (!snapshot.exists()) {
    return null
  }
  return {id: snapshot.id, ...snapshot.data()}
}

export const getPrinterStatusDoc = async (deviceId: string): Promise<PrintStoryDoc | null> => {
  const db = getFirestore()
  const snapshot = await getDoc(doc(db, 'printerStatus', deviceId))
  if (!snapshot.exists()) {
    return null
  }
  return {id: snapshot.id, deviceId, ...snapshot.data()}
}

export const getRenderJobsForContent = async (
  contentId: string,
  codeIds: string[]
): Promise<PrintStoryDoc[]> => {
  const byContentId = await getDocsByContentId('renderJobs', contentId, 'jobId')
  if (byContentId.length > 0 || codeIds.length === 0) {
    return byContentId
  }

  const db = getFirestore()
  const chunks: string[][] = []
  for (let i = 0; i < codeIds.length; i += 10) {
    chunks.push(codeIds.slice(i, i + 10))
  }

  const snapshots = await Promise.all(
    chunks.map((chunk) =>
      getDocs(firebaseQuery(collection(db, 'renderJobs'), where('codeId', 'in', chunk)))
    )
  )

  const jobs = snapshots.flatMap((snapshot) => mapDocs(snapshot, 'jobId'))
  return Array.from(new Map(jobs.map((job) => [job.id, job])).values())
}
